/**
 * Noticing that the page itself is out of date, and doing something about it.
 *
 * The entry point is served with `no-cache` and the bundle beneath it is hashed,
 * so in theory a reload is enough. In practice a phone is the awkward case: there
 * is no way to force a hard refresh, a tab can sit suspended for days, and a
 * browser that has decided it has a good copy of the page will happily keep
 * serving it. The symptom is somebody unable to log in after a deploy until they
 * wipe the whole browser cache, which is not a thing to ask of anybody.
 *
 * So the page checks for itself. It knows which build it is, it asks the server
 * which build is current, and when the two disagree it replaces itself with the
 * new one, going out of its way to make sure nothing between here and the server
 * can answer with the old one.
 */

/** How often to look, while the page is open and visible. Ten minutes. */
const CHECK_EVERY_MS = 10 * 60 * 1000;

/**
 * Which build this page is.
 *
 * Written into the document at build time. Falling back to the source of the
 * first hashed script covers a build that predates the marker, since that name
 * changes whenever the bundle does.
 */
export function currentBuild(): string {
  const marked = document.querySelector('meta[name="build"]')?.getAttribute('content');
  if (marked && marked !== '__BUILD_ID__') return marked;

  const script = [...document.querySelectorAll('script[src]')]
    .map((tag) => tag.getAttribute('src') ?? '')
    .find((src) => /assets\/.*\.js/.test(src));

  return script ?? 'dev';
}

/**
 * Which build the server is serving now.
 *
 * The entry point is fetched rather than an API of its own, because the entry
 * point is the thing that has to change: whatever a proxy or a browser has
 * cached, this is the request that finds out.
 */
export async function latestBuild(): Promise<string | null> {
  try {
    const response = await fetch(`./index.html?checked=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'cache-control': 'no-cache' },
    });
    if (!response.ok) return null;

    const html = await response.text();

    const marked = /<meta\s+name="build"\s+content="([^"]+)"/i.exec(html)?.[1];
    if (marked && marked !== '__BUILD_ID__') return marked;

    const script = /src="([^"]*assets\/[^"]*\.js)"/i.exec(html)?.[1];
    return script ?? null;
  } catch {
    // Offline, or the server is mid restart. Not knowing is not an update.
    return null;
  }
}

/** Whether what is being served is something other than what is running here. */
export async function updateWaiting(): Promise<boolean> {
  const latest = await latestBuild();
  if (latest === null) return false;

  return latest !== currentBuild();
}

/**
 * Replaces this page with the current one.
 *
 * An ordinary reload first, with everything a browser might be holding the old
 * version in cleared out of the way. That is enough almost everywhere, and it
 * leaves the address alone.
 *
 * Only if that has already been tried for this build and the page came back the
 * same does it fall back to a changing parameter on the address, which nothing
 * between here and the server can have a cached answer for. Tried in that order
 * because the parameter is ugly, it sticks around, and it should never be the
 * first thing anybody sees.
 */
export async function applyUpdate(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {
    // Nothing registered, or the browser will not say. Either way, carry on.
  }

  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } catch {
    // Same again: worth trying, never worth failing over.
  }

  const build = currentBuild();

  if (remember(RELOADED_KEY) !== build) {
    remember(RELOADED_KEY, build);
    window.location.reload();
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set('v', Date.now().toString(36));
  window.location.replace(url.toString());
}

const RELOADED_KEY = 'inverse.reloadedFor';
const TRIED_KEY = 'inverse.updateTried';

function remember(key: string, value?: string): string | null {
  try {
    if (value === undefined) return window.sessionStorage.getItem(key);

    window.sessionStorage.setItem(key, value);
    return value;
  } catch {
    // Storage refused, which Discord's sandbox sometimes does. Everything below
    // still works; it just cannot tell whether it has been here before.
    return null;
  }
}

/**
 * Takes the version parameter back off the address once it has done its job.
 *
 * It only exists to defeat a cache for one request. Leaving it on means every
 * link somebody copies carries it, and the next reload carries it again.
 */
export function tidyAddress(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('v')) return;

  url.searchParams.delete('v');
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * Takes the newer build straight away, at most once per attempt.
 *
 * For the login screen, where there is nothing to interrupt: a stale page there
 * is somebody unable to get in at all. Which build was tried is remembered for
 * the session, so a mismatch that somehow survives the reload turns into one
 * wasted attempt rather than a page that reloads for ever.
 */
export async function updateBeforeLogin(): Promise<boolean> {
  tidyAddress();

  const latest = await latestBuild();
  if (latest === null || latest === currentBuild()) return false;
  if (remember(TRIED_KEY) === latest) return false;

  remember(TRIED_KEY, latest);
  await applyUpdate();
  return true;
}

/**
 * Watches for a new build for as long as the page is open.
 *
 * Checked on a timer, whenever the tab is looked at again, and whenever the
 * network comes back, since those are the three moments a phone that has been
 * asleep for a day rejoins the world.
 */
export function watchForUpdates(onWaiting: () => void): () => void {
  let stopped = false;

  const look = (): void => {
    if (stopped || document.visibilityState === 'hidden') return;

    void updateWaiting().then((waiting) => {
      if (waiting && !stopped) onWaiting();
    });
  };

  const timer = window.setInterval(look, CHECK_EVERY_MS);
  document.addEventListener('visibilitychange', look);
  window.addEventListener('online', look);

  return () => {
    stopped = true;
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', look);
    window.removeEventListener('online', look);
  };
}
